import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BookOpen, Upload, Crown, Trash2, Target, Save } from "lucide-react";

const inputCls = "w-full bg-black/50 border border-smasher-gold/25 rounded-xl px-4 py-2.5 text-white outline-none focus:border-smasher-gold text-sm";
const errMsg = (err) => formatApiErrorDetail(err.response?.data?.detail);

export function EbookUploadPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/ebook/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/ebook", fd);
      toast.success("Ebook uploaded — buyers can download it now");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="panel p-6" data-testid="admin-ebook-panel">
      <h2 className="font-heading font-bold text-xl text-white mb-1 flex items-center gap-2"><BookOpen className="w-5 h-5 text-smasher-gold" /> Ebook File</h2>
      <p className="text-slate-500 text-xs mb-4">Upload the Football Edition PDF. Anyone who buys the ebook gets a download button on the Ebook page and in My Area.</p>
      <div className="text-sm mb-4" data-testid="admin-ebook-status">
        {status?.file ? (
          <span className="text-emerald-400">Current file: {status.file.original_filename} ({(status.file.size / 1048576).toFixed(2)} MB)</span>
        ) : <span className="text-orange-400">No ebook uploaded yet — buyers will see "being prepared".</span>}
      </div>
      <label className={`btn-gold px-5 py-2.5 rounded-xl font-bold inline-flex items-center gap-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
        <Upload className="w-4 h-4" /> {busy ? "UPLOADING…" : status?.file ? "REPLACE PDF" : "UPLOAD PDF"}
        <input type="file" accept="application/pdf,.pdf" onChange={upload} className="hidden" data-testid="admin-ebook-input" />
      </label>
    </div>
  );
}

export function MemberPostsPanel() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", tier: "starter", category: "Daily Best Bet" });
  const load = () => api.get("/admin/member-posts").then((r) => setPosts(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/member-posts", form);
      toast.success("Posted to members");
      setForm({ ...form, title: "", body: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const del = async (id) => { await api.delete(`/admin/member-posts/${id}`).catch(() => {}); load(); };

  return (
    <div className="panel p-6" data-testid="admin-member-posts-panel">
      <h2 className="font-heading font-bold text-xl text-white mb-1 flex items-center gap-2"><Crown className="w-5 h-5 text-smasher-gold" /> Members-Only Posts</h2>
      <p className="text-slate-500 text-xs mb-4">Starter posts are seen by Starter + Pro members. Pro posts are seen by Pro members only.</p>
      <form onSubmit={add} className="space-y-3 mb-4" data-testid="admin-member-post-form">
        <div className="grid grid-cols-2 gap-2">
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className={inputCls} data-testid="admin-member-post-tier">
            <option value="starter">Starter + Pro</option><option value="pro">Pro only</option>
          </select>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="admin-member-post-category">
            {["Daily Best Bet", "Premium Pick", "Player Props", "Parlay", "Analysis", "Injury & News"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="admin-member-post-title" />
        <textarea required rows={4} placeholder="Write the pick or breakdown…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} data-testid="admin-member-post-body" />
        <button className="btn-gold px-5 py-2.5 rounded-xl font-bold w-full" data-testid="admin-member-post-submit">POST TO MEMBERS</button>
      </form>
      <div className="space-y-1.5 max-h-64 overflow-auto no-scrollbar">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5 text-sm">
            <span className="text-slate-300 truncate"><span className={p.tier === "pro" ? "text-smasher-gold font-bold" : "text-slate-500 font-bold"}>{p.tier.toUpperCase()}</span> · {p.title}</span>
            <button onClick={() => del(p.id)} className="text-red-400 hover:text-red-300 shrink-0" data-testid={`admin-member-post-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {!posts.length && <p className="text-slate-500 text-sm">No member posts yet.</p>}
      </div>
    </div>
  );
}

function LineRow({ ev }) {
  const [v, setV] = useState({ spread: ev.spread || "", total: ev.total || "", moneyline: ev.moneyline || "" });
  const save = async () => {
    try {
      await api.put(`/admin/events/${ev.id}/odds`, v);
      toast.success(`Line saved: ${ev.team_b.abbr} @ ${ev.team_a.abbr}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="grid grid-cols-[1fr_repeat(3,90px)_40px] gap-2 items-center border-b border-white/5 py-1.5 text-sm" data-testid={`admin-line-row-${ev.id}`}>
      <span className="text-slate-300 truncate"><span className="text-smasher-gold font-bold">{ev.league}</span> {ev.team_b.name} @ {ev.team_a.name} · {new Date(ev.start).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
      {["spread", "total", "moneyline"].map((k) => (
        <input key={k} placeholder={k === "spread" ? "NYY -1.5" : k === "total" ? "O/U 8.5" : "NYY -150"} value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="bg-black/50 border border-smasher-gold/25 rounded-lg px-2 py-1.5 text-white text-xs outline-none focus:border-smasher-gold" data-testid={`admin-line-${k}-${ev.id}`} />
      ))}
      <button onClick={save} className="text-smasher-gold hover:text-smasher-orange" title="Save" data-testid={`admin-line-save-${ev.id}`}><Save className="w-4 h-4" /></button>
    </div>
  );
}

export function GameLinesPanel() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events").then((r) => setEvents((r.data || []).filter((e) => e.status !== "final" && e.type === "team"))).catch(() => {});
  }, []);
  return (
    <div className="panel p-6" data-testid="admin-lines-panel">
      <h2 className="font-heading font-bold text-xl text-white mb-1 flex items-center gap-2"><Target className="w-5 h-5 text-smasher-gold" /> Game Lines (Spread · Total · Moneyline)</h2>
      <p className="text-slate-500 text-xs mb-4">Your current sports data plan doesn't include odds, so lines are entered here. They show on the game cards instantly and stay put through data refreshes.</p>
      <div className="space-y-0.5 max-h-96 overflow-auto no-scrollbar">
        {events.map((ev) => <LineRow key={ev.id} ev={ev} />)}
        {!events.length && <p className="text-slate-500 text-sm">No live or upcoming games in the feed right now.</p>}
      </div>
    </div>
  );
}

export function PicksPanel() {
  const [st, setSt] = useState(null);
  const load = () => api.get("/admin/picks/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const refresh = async (football) => {
    await api.post("/admin/picks/refresh", null, { params: { football } }).catch(() => {});
    toast.success("Refreshing picks and sports updates — about a minute");
    setSt((s) => ({ ...s, running: true }));
    setTimeout(load, 90000);
  };
  return (
    <div className="panel p-6" data-testid="admin-picks-panel">
      <h2 className="font-heading font-bold text-xl text-white mb-1 flex items-center gap-2"><Target className="w-5 h-5 text-smasher-gold" /> Daily Top 5 Picks & Sports Updates</h2>
      <p className="text-slate-500 text-xs mb-4">Runs every day at 11am ET. NBA: 2 fresh picks daily. Football (NFL + college): 5 picks set every Tuesday for the week (or sooner if those games are all played). Plus fresh news briefs in our voice.</p>
      <div className="text-sm text-slate-300 mb-4" data-testid="admin-picks-status">
        {st?.running ? "Refreshing now…" : st?.last_run ? `Last run ${new Date(st.last_run).toLocaleString()} · ${st.picks_found} picks scanned · ${st.briefs} updates${st.football_refreshed ? " · football week reset" : ""}` : "Not run yet."}
        {st?.errors && Object.keys(st.errors).length > 0 && <div className="text-orange-400 text-xs mt-1">Issues: {Object.keys(st.errors).join(", ")}</div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => refresh(false)} disabled={st?.running} className="btn-gold px-5 py-2.5 rounded-xl font-bold disabled:opacity-60" data-testid="admin-picks-refresh">REFRESH NBA + NEWS</button>
        <button onClick={() => refresh(true)} disabled={st?.running} className="btn-ghost-gold px-5 py-2.5 rounded-xl font-bold disabled:opacity-60" data-testid="admin-picks-refresh-football">ALSO RESET FOOTBALL WEEK</button>
      </div>
    </div>
  );
}
